// GRAVY-10073: shared helper for the staffing wizards' "Hours This Week"
// calculation. Returns the Monday-anchored UTC week boundaries so the
// frontend's per-row at-risk highlighting agrees with the backend's
// `Workers::Overtime::WeeklyHoursService` bucketing (UTC, Monday week
// start). Without a shared anchor the cell can flip red for shifts that
// don't actually impact this week's 40h budget.

export type WeekRange = { startMs: number; endMs: number };

export const currentWeekRangeUtc = (now: Date = new Date()): WeekRange => {
  const day = now.getUTCDay(); // 0 = Sun, 1 = Mon, … 6 = Sat
  const daysFromMonday = (day + 6) % 7;
  const monday = new Date(
    Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate() - daysFromMonday,
      0,
      0,
      0,
      0,
    ),
  );
  const nextMonday = new Date(monday.getTime() + 7 * 24 * 60 * 60 * 1000);
  return { startMs: monday.getTime(), endMs: nextMonday.getTime() };
};

export const isInCurrentWeekUtc = (
  startAtIso: string,
  range: WeekRange = currentWeekRangeUtc(),
): boolean => {
  const ms = new Date(startAtIso).getTime();
  if (!Number.isFinite(ms)) return false;
  return ms >= range.startMs && ms < range.endMs;
};

// Sums shift duration (hours) for the subset of `shifts` whose start_at
// falls within the current Monday-anchored UTC week. Used by the staffing
// wizards to feed `HoursThisWeekCell.selectedShiftHours` with a week-
// aware value (rather than the cumulative total across all selected
// weeks).
//
// GRAVY-10073 break-aware: each shift may carry `mandatory_break_minutes`
// — the helper subtracts the value from gross so the per-row at-risk red
// matches the backend's net-of-break projection. Missing/0 break
// preserves the legacy gross behavior. `Math.max(..., 0)` clamps the
// data-corruption case where break > gross.
export const sumCurrentWeekShiftHours = (
  shifts: Array<{
    start_at: string;
    end_at: string;
    mandatory_break_minutes?: number;
  }>,
): number => {
  const range = currentWeekRangeUtc();
  let total = 0;
  for (const s of shifts) {
    if (!isInCurrentWeekUtc(s.start_at, range)) continue;
    const startMs = new Date(s.start_at).getTime();
    const endMs = new Date(s.end_at).getTime();
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) continue;
    const breakHours = (s.mandatory_break_minutes ?? 0) / 60;
    const netHours = (endMs - startMs) / 3_600_000 - breakHours;
    total += Math.max(netHours, 0);
  }
  return Number(total.toFixed(2));
};
